/**
 * Canonical RBAC module + action constants. Keep in sync with
 * apps/backend/src/common/rbac/modules.ts.
 */
export const RBAC_MODULES = [
  "dashboard",
  "businesses",
  "owners",
  "approvals",
  "customers",
  "bookings",
  "subscriptions",
  "revenue",
  "featured",
  "reviews",
  "notifications",
  "packages",
  "planSettings",
  "platformSettings",
  "branding",
  "staffManagement",
] as const;

export type RbacModule = (typeof RBAC_MODULES)[number];

export const RBAC_ACTIONS = [
  "view",
  "create",
  "edit",
  "delete",
  "export",
  "approve",
  "reject",
  "suspend",
  "moderate",
  "refund",
  "broadcast",
] as const;

export type RbacAction = (typeof RBAC_ACTIONS)[number];

export const MODULE_LABELS: Record<RbacModule, string> = {
  dashboard: "Dashboard",
  businesses: "Businesses",
  owners: "Owners",
  approvals: "Approvals",
  customers: "Customers",
  bookings: "Bookings",
  subscriptions: "Subscriptions",
  revenue: "Revenue",
  featured: "Featured",
  reviews: "Reviews",
  notifications: "Notifications",
  packages: "Packages",
  planSettings: "Plan Settings",
  platformSettings: "Platform Settings",
  branding: "Branding",
  staffManagement: "Staff Management",
};

export const ACTION_LABELS: Record<RbacAction, string> = {
  view: "View",
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  export: "Export",
  approve: "Approve",
  reject: "Reject",
  suspend: "Suspend",
  moderate: "Moderate",
  refund: "Refund",
  broadcast: "Broadcast",
};

export const MODULE_ACTIONS: Record<RbacModule, readonly RbacAction[]> = {
  dashboard: ["view"],
  businesses: [
    "view",
    "create",
    "edit",
    "delete",
    "approve",
    "reject",
    "suspend",
  ],
  owners: ["view", "create", "edit", "suspend"],
  approvals: ["view", "approve", "reject"],
  customers: ["view", "edit", "delete", "export"],
  bookings: ["view", "edit", "export", "refund"],
  subscriptions: ["view", "edit", "export"],
  revenue: ["view", "export"],
  featured: ["view", "edit"],
  reviews: ["view", "edit", "delete", "moderate"],
  notifications: ["view", "broadcast"],
  packages: ["view", "create", "edit", "delete"],
  planSettings: ["view", "create", "edit", "delete"],
  platformSettings: ["view", "edit"],
  branding: ["view", "edit"],
  staffManagement: ["view", "create", "edit", "delete"],
};

export const SYSTEM_ROLE_NAMES = {
  SUPER_ADMIN: "Super Admin",
  OPERATIONS: "Operations",
  ACCOUNTANT: "Accountant",
  SUPPORT: "Support",
} as const;

export type PermissionFlag =
  | "canView"
  | "canCreate"
  | "canEdit"
  | "canDelete"
  | "canExport"
  | "canApprove"
  | "canReject"
  | "canSuspend"
  | "canModerate"
  | "canRefund"
  | "canBroadcast";

export const ACTION_TO_FLAG: Record<RbacAction, PermissionFlag> = {
  view: "canView",
  create: "canCreate",
  edit: "canEdit",
  delete: "canDelete",
  export: "canExport",
  approve: "canApprove",
  reject: "canReject",
  suspend: "canSuspend",
  moderate: "canModerate",
  refund: "canRefund",
  broadcast: "canBroadcast",
};

/**
 * Sidebar nav → RBAC module mapping. Used by the sidebar to hide nav items
 * the current user has no view permission for.
 */
export const ROUTE_MODULE_MAP: Record<string, RbacModule> = {
  "/": "dashboard",
  "/businesses": "businesses",
  "/locations": "businesses",
  "/owners": "owners",
  "/approvals": "approvals",
  "/customers": "customers",
  "/bookings": "bookings",
  "/subscriptions": "subscriptions",
  "/revenue": "revenue",
  "/featured": "featured",
  "/reviews": "reviews",
  "/notifications": "notifications",
  "/packages": "packages",
  "/plan-settings": "planSettings",
  "/admin-accounts": "staffManagement",
  "/platform-settings": "platformSettings",
  "/branding": "branding",
  "/staff": "staffManagement",
  "/roles": "staffManagement",
};
